import { useState, useEffect, useRef, useCallback } from "react";
import { StickyNote, ChevronDown, ChevronUp } from "lucide-react";
import api from "../../lib/api";
import { Dialog, DialogContent } from "../ui/dialog";

export const GLOW = { red: "shadow-[0_0_0_2px_rgba(239,68,68,.55),0_0_14px_rgba(239,68,68,.35)]", yellow: "shadow-[0_0_0_2px_rgba(245,158,11,.6),0_0_14px_rgba(245,158,11,.35)]", green: "shadow-[0_0_0_2px_rgba(16,185,129,.6),0_0_14px_rgba(16,185,129,.35)]", none: "shadow-sm border border-slate-200" };
export const glowFor = (done, total) => (total === 0 ? "none" : done === 0 ? "red" : done < total ? "yellow" : "green");
export const pctColor = (p) => `hsl(${Math.round(Math.max(0, Math.min(100, p)) * 1.25)} 78% 45%)`;

export const EVENT_COLORS = { sky: ["#e0f2fe", "#7dd3fc"], emerald: ["#d1fae5", "#6ee7b7"], amber: ["#fef3c7", "#fcd34d"], rose: ["#ffe4e6", "#fda4af"], violet: ["#ede9fe", "#c4b5fd"], slate: ["#f1f5f9", "#cbd5e1"], gold: ["#fbf3d5", "#D4AF37"] };

export function Card({ title, icon: I, glow = "none", action, children, className = "", testid }) {
  return (
    <section data-testid={testid} className={`bg-white/95 backdrop-blur rounded-xl p-3.5 transition-[box-shadow] duration-500 ${GLOW[glow]} ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <h3 className="font-display font-bold text-navy-800 text-sm flex items-center gap-1.5 min-w-0">{I && <I className="w-4 h-4 text-gold-dark shrink-0" />}<span className="break-words">{title}</span></h3>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Check({ checked, onChange, testid }) {
  return <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#0B1F3A] cursor-pointer shrink-0" data-testid={testid} />;
}

export function Counter({ label, value, onChange, unit = "", step = 1, max, testid }) {
  const hit = max && value >= max;
  return (
    <div className="flex items-center justify-between py-1" data-testid={testid}>
      <span className={`text-xs ${hit ? "text-emerald-600 font-semibold" : "text-slate-600"}`}>{label}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onChange(Math.max(0, value - step))} className="w-6 h-6 rounded-md bg-slate-100 hover:bg-slate-200 text-navy-800 text-sm font-bold">−</button>
        <input type="number" value={value} onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))} className="w-14 text-center text-xs font-semibold text-navy-800 border border-slate-200 rounded-md py-0.5" />
        <button onClick={() => onChange(value + step)} className="w-6 h-6 rounded-md bg-navy-800 hover:bg-navy-700 text-white text-sm font-bold">+</button>
        <span className="text-[10px] text-slate-400 w-14 whitespace-nowrap">{max ? `/ ${max}${unit}` : unit}</span>
      </div>
    </div>
  );
}

export function ProgressBar({ pct, label = "Today's overall progress", testid = "day-progress" }) {
  const p = Math.round(pct || 0);
  return (
    <div className="relative h-8 rounded-full bg-slate-200/80 overflow-hidden shadow-inner" data-testid={testid} title={label}>
      <div className="absolute inset-y-0 left-0 rounded-full transition-[width,background-color] duration-700" style={{ width: `${Math.max(p, 2)}%`, backgroundColor: pctColor(p) }} />
      <div className={`absolute inset-0 flex items-center justify-center gap-2 text-xs font-bold tabular-nums ${p > 55 ? "text-white" : "text-navy-800"}`}>
        <span className="font-display text-sm" data-testid="day-progress-pct">{p}%</span><span className="font-normal opacity-80 hidden sm:inline"><span>· </span><span>{label}</span></span>
      </div>
    </div>
  );
}

export function Collapsible({ title, icon: I, defaultOpen = false, children, testid, summary }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-white/95 backdrop-blur rounded-xl border border-slate-200 shadow-sm" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-3.5 py-2.5 text-left" data-testid={`${testid}-toggle`}>
        <span className="font-display font-bold text-navy-800 text-sm flex items-center gap-1.5">{I && <I className="w-4 h-4 text-gold-dark" />}{title}</span>
        <span className="flex items-center gap-3 text-[11px] text-slate-500">{summary}{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</span>
      </button>
      {open && <div className="px-3.5 pb-3.5">{children}</div>}
    </section>
  );
}

const ZONES = [["São Paulo", "America/Sao_Paulo", "br"], ["Berlin", "Europe/Berlin", "de"], ["Cairo", "Africa/Cairo", "eg"], ["Beijing", "Asia/Shanghai", "cn"]];
export function WorldClocks() {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(t); }, []);
  return (
    <div className="grid grid-cols-4 gap-2" data-testid="world-clocks">
      {ZONES.map(([city, tz, cc]) => {
        const parts = Object.fromEntries(new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "numeric", minute: "numeric", second: "numeric", hour12: false }).formatToParts(new Date()).map((p) => [p.type, Number(p.value)]));
        const h = parts.hour % 12, m = parts.minute, s = parts.second;
        const hand = (deg, len, w, color) => <line x1="50" y1="50" x2={50 + len * Math.sin((deg * Math.PI) / 180)} y2={50 - len * Math.cos((deg * Math.PI) / 180)} stroke={color} strokeWidth={w} strokeLinecap="round" />;
        return (
          <div key={city} className="text-center" data-testid={`clock-${city}`}>
            <svg viewBox="0 0 100 100" className="w-14 h-14 mx-auto">
              <defs><clipPath id={`clip-${cc}`}><circle cx="50" cy="50" r="44" /></clipPath></defs>
              <circle cx="50" cy="50" r="46" fill="#fff" stroke="#0B1F3A" strokeWidth="3" />
              <image href={`https://flagcdn.com/w160/${cc}.png`} x="6" y="6" width="88" height="88" preserveAspectRatio="xMidYMid slice" clipPath={`url(#clip-${cc})`} opacity="0.4" />
              {[...Array(12)].map((_, i) => <line key={i} x1="50" y1="8" x2="50" y2={i % 3 === 0 ? 15 : 11} stroke="#0B1F3A" strokeWidth={i % 3 === 0 ? 2.5 : 1.2} transform={`rotate(${i * 30} 50 50)`} />)}
              {hand(h * 30 + m / 2, 24, 4, "#0B1F3A")}{hand(m * 6 + s / 10, 34, 2.5, "#0B1F3A")}{hand(s * 6, 38, 1, "#D4AF37")}
              <circle cx="50" cy="50" r="2.5" fill="#D4AF37" />
            </svg>
            <p className="text-[10px] font-semibold text-navy-800 leading-tight">{city}</p>
            <p className="text-[10px] text-slate-500 tabular-nums">{new Intl.DateTimeFormat("en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit" }).format(new Date())}</p>
          </div>
        );
      })}
    </div>
  );
}

export function NoteButton({ event, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(event.notes || "");
  const t = useRef();
  const save = useCallback((v) => { clearTimeout(t.current); t.current = setTimeout(() => api.put(`/appointments/${event.id}`, { notes: v }).then(() => onSaved?.(v)), 500); }, [event.id, onSaved]);
  return (
    <>
      <button title={event.notes ? event.notes : "Add note"} onClick={(e) => { e.stopPropagation(); setOpen(true); }} className={`p-0.5 rounded shrink-0 ${event.notes ? "text-gold-dark" : "text-slate-300 hover:text-navy-800"}`} data-testid={`note-btn-${event.id}`}><StickyNote className="w-3 h-3" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`note-pop-${event.id}`}>
          <p className="text-[10px] font-bold uppercase text-gold-dark tracking-wider">Note · autosaves</p>
          <h3 className="font-display font-bold text-navy-800 break-words">{event.title}</h3>
          <textarea autoFocus value={text} onChange={(e) => { setText(e.target.value); save(e.target.value); }} rows={7} placeholder="What happened, what to remember, follow-ups…" className="w-full text-sm border border-slate-200 rounded-md p-2 focus:outline-none focus:border-gold" data-testid={`note-text-${event.id}`} />
        </DialogContent>
      </Dialog>
    </>
  );
}
